import { useState, useEffect } from 'react'
import PageSEO from '@/components/SEO/PageSEO'
import ContentSections from '@/components/Sections/ContentSections'
import classes from './Page.module.scss'
import ResponsiveImage from '@/components/ResponsiveImage'
import { getRecentArticlesHandles } from '@/utils/getRecentArticleHandles'
import ResponsiveComponent from '@/components/ResponsiveComponent'
import { getPageByHandle, getInfoPageByHandle } from '@/utils/getPages'
import { getSanityClient } from '@/services/sanityClient'
import groq from 'groq'
import { PreviewButton } from '@/components/Preview/PreviewButton'
import { useMarketingContext } from '@/context/MarketingContext'
import getCollectionListProducts from '@/utils/getCollectionListProducts'
import getCollectionHeroProducts from '@/utils/getCollectionHeroProducts'
import { useHeaderContext } from '@/context/HeaderContext'
import { useHeadlessCheckoutContext } from '@/context/HeadlessCheckoutContext'
import { getFeaturedSubscriptionProducts } from '@/utils/getFeaturedSubscriptionProducts'

const illustrationImgLoader = ({ src }) => {
  return `${src}?w=750`
}

export default function DynamicPage({ page, preview, previewToken }) {
  const marketingContext = useMarketingContext()
  const { data, applySingleDiscount } = useHeadlessCheckoutContext()
  const [loading, setLoading] = useState(false)

  const { setClosedDoors } = useHeaderContext()
  setClosedDoors(page?.fields?.experience?.disableMenuSystem)

  useEffect(() => {
    async function setLPDiscount(discount) {
      const toProcess = data?.cart
        ? !data?.cart?.discountCodes.some((d) => d.code?.includes(discount))
        : false
      if (!loading && toProcess) {
        setLoading(true)
        try {
          await applySingleDiscount(discount)
        } finally {
          // setLoading(false);
        }
      }
    }
    if (page?.fields?.experience?.autoDiscount) {
      setLPDiscount(page?.fields?.experience?.autoDiscount)
    }
  }, [data?.cart])

  if (page.fields?.marketing) {
    marketingContext.setUTMSource(page.fields?.marketing?.source)
    marketingContext.setUTMMedium(page.fields?.marketing?.medium)
    marketingContext.setUTMCampaign(page.fields?.marketing?.campaign)
    marketingContext.setUTMContent('')
  }

  if (page.type === 'infoPage') {
    const {
      header,
      leftContent,
      rightContent,
      bottomContent,
      desktopIllustration,
      mobileIllustration,
    } = page.fields
    return (
      <>
        <div className={`${classes['info-page']} info-page container`}>
          <PageSEO seo={page.fields.seo} />
          <div className={classes['info-page__left']}>
            <h1 className={classes['info-page__header']}>{header}</h1>
            {leftContent && <ContentSections sections={leftContent} />}
            <ResponsiveComponent
              desktopBreakpoint={'md'}
              desktop={
                desktopIllustration && (
                  <div className={classes['info-page__illustration-image']}>
                    <ResponsiveImage
                      loader={illustrationImgLoader}
                      priority={true}
                      src={desktopIllustration.asset.url}
                      layout="fill"
                      alt={desktopIllustration.asset.alt || ''}
                    />
                  </div>
                )
              }
              mobile={
                mobileIllustration && (
                  <div className={classes['info-page__illustration-image']}>
                    <ResponsiveImage
                      loader={illustrationImgLoader}
                      priority={true}
                      src={mobileIllustration.asset.url}
                      layout="fill"
                      alt={mobileIllustration.asset.alt || ''}
                    />
                  </div>
                )
              }
            />
          </div>
          <div className={classes['info-page__right']}>
            {rightContent && <ContentSections sections={rightContent} />}
          </div>
        </div>
        {bottomContent && <ContentSections sections={bottomContent} />}
      </>
    )
  }

  return (
    <div className="standard-page">
      {preview && previewToken && <PreviewButton />}
      <PageSEO seo={page.fields.seo} />
      <ContentSections sections={page.fields.content} />
    </div>
  )
}

export async function getStaticPaths() {
  const pages = await getSanityClient().fetch(
    groq`*[_type in ["infoPage", "page"]]{ handle { current } }`,
  )
  const handles = pages.map((page) => ({
    params: { handle: page.handle.current },
  }))

  return {
    paths: handles,
    fallback: 'blocking',
  }
}

export async function getStaticProps({ params, draftMode }) {
  const preview = draftMode || false
  const previewToken = preview ? process.env.SANITY_READ_TOKEN : ``
  if (preview && !previewToken) {
    throw new Error(
      `Preview mode is active, but SANITY_READ_TOKEN is not set in environment variables`,
    )
  }
  const sanityClient = getSanityClient(previewToken)

  const [page, infoPage] = await Promise.all([
    getPageByHandle(params.handle, sanityClient),
    getInfoPageByHandle(params.handle, sanityClient),
  ])

  if (!page && !infoPage) {
    return {
      notFound: true,
    }
  }

  const fullRefPage = page ? page : infoPage

  if (
    fullRefPage?.fields?.content?.some(
      (content) => content._type === 'featuredBlogContent',
    )
  ) {
    await getRecentArticlesHandles(fullRefPage.fields.content)
  }

  if (
    fullRefPage?.fields?.content?.some(
      (content) => content._type === 'collectionList',
    )
  ) {
    await getCollectionListProducts(fullRefPage)
  }

  if (
    fullRefPage?.fields?.content?.some(
      (content) => content._type === 'collectionHero',
    )
  ) {
    await getCollectionHeroProducts(fullRefPage)
  }

  if (
    fullRefPage?.fields?.content?.some(
      (content) => content._type === 'featuredSubscriptionBlock',
    )
  ) {
    await getFeaturedSubscriptionProducts(fullRefPage)
  }

  return {
    props: {
      preview,
      previewToken,
      page: fullRefPage,
    },
  }
}
